<script lang="ts">
export default {
  name: 'Reporting',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { ServerIcon, AlertCircleIcon, FileTextIcon, RefreshCwIcon, FilterIcon, ShieldIcon } from '@lucide/vue';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import Button from '@/components/ui/UIButton.vue';
import Combobox, { type ComboboxOption } from '@/components/v2/Combobox.vue';
import { adminService, type LambdaFunction, type LambdaLog, type LambdaLogsParams, type BeanstalkEnvironment, type BeanstalkLog, type BeanstalkLogsParams, type DlpAuditLog, type DlpAuditParams } from '@/services/AdminService';
import userService from '@/services/UserService';

const currentTab = ref<string>('beanstalk');

// -----
// data
// -----

const loading = ref(true);
const error = ref<string | null>(null);
const lambdaFunctions = ref<LambdaFunction[]>([]);

// Beanstalk logs state
const selectedBeanstalkEnvironment = ref<string>('');
const loadedBeanstalkEnvironment = ref<string>(''); // Track which environment the current logs are from
const beanstalkEnvironments = ref<BeanstalkEnvironment[]>([]);
const beanstalkLogs = ref<BeanstalkLog[]>([]);
const beanstalkLogsLoading = ref(false);
const beanstalkLogsError = ref<string | null>(null);
const beanstalkLogsCount = ref(0);

// Beanstalk filter state
const beanstalkLogLevel = ref<string>('');
const beanstalkCorrelationId = ref<string>('');
const beanstalkUserId = ref<string>('');
const beanstalkLimit = ref<number>(100);

// User options for combobox
const userOptions = ref<ComboboxOption[]>([]);

const logLevelOptions: ComboboxOption[] = [
  { label: 'All levels', value: '' },
  { label: 'ERROR', value: 'ERROR' },
  { label: 'WARN', value: 'WARN' },
  { label: 'INFO', value: 'INFO' },
  { label: 'DEBUG', value: 'DEBUG' },
];

const beanstalkEnvironmentOptions = computed<ComboboxOption[]>(() =>
  beanstalkEnvironments.value.map(env => ({ label: env.name, value: env.name }))
);

const lambdaFunctionOptions = computed<ComboboxOption[]>(() =>
  lambdaFunctions.value.map(func => ({ label: func.name, value: func.name }))
);

const userIdToNameMap = ref<Map<number, string>>(new Map());
const beanstalkStartTime = ref<string>('');
const beanstalkEndTime = ref<string>('');

// Lambda logs state
const selectedLambdaFunction = ref<string>('');
const loadedLambdaFunction = ref<string>(''); // Track which function the current logs are from
const lambdaLogs = ref<LambdaLog[]>([]);
const lambdaLogsLoading = ref(false);
const lambdaLogsError = ref<string | null>(null);
const lambdaLogsCount = ref(0);

// Lambda filter state
const lambdaLogLevel = ref<string>('');
const lambdaLimit = ref<number>(100);
const lambdaStartTime = ref<string>('');
const lambdaEndTime = ref<string>('');

// DLP audit logs state
const dlpLogs = ref<DlpAuditLog[]>([]);
const dlpLogsLoading = ref(false);
const dlpLogsError = ref<string | null>(null);

// DLP filter state
const dlpUserId = ref<string>('');
const dlpStartDate = ref<string>('');
const dlpEndDate = ref<string>('');

// -----
// lifecycle
// -----

async function populateUsers() {
  const users = await userService.getAllUsers();
  userOptions.value = users
    .filter(user => user.name || user.userEmail)
    .map(user => ({
      label: `${user.name || user.userEmail} (UserID: ${user.id})`,
      value: String(user.id),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  
  // Build lookup map for displaying user names in logs
  users.forEach(user => {
    userIdToNameMap.value.set(user.id, user.name || user.userEmail);
  });
}

onMounted(async () => {
  // Set default time ranges
  setLambdaDefaultTimeRange();
  setBeanstalkDefaultTimeRange();
  setDlpDefaultTimeRange();
  
  try {
    const [lambdaResponse, beanstalkResponse] = await Promise.all([
      adminService.getLambdaFunctions(),
      adminService.getBeanstalkEnvironments(),
      populateUsers(),
    ]);
    
    lambdaFunctions.value = lambdaResponse.functions;
    beanstalkEnvironments.value = beanstalkResponse.environments;
  } catch (err) {
    console.error('Failed to load data:', err);
    error.value = 'Failed to load Lambda functions and Beanstalk environments. Please check your permissions and try again.';
  } finally {
    loading.value = false;
  }
});

// -----
// methods
// -----

const loadBeanstalkLogs = async () => {
  if (!selectedBeanstalkEnvironment.value) {
    beanstalkLogsError.value = 'Please select a Beanstalk environment first.';
    return;
  }

  beanstalkLogsLoading.value = true;
  beanstalkLogsError.value = null;

  try {
    const params: BeanstalkLogsParams = {
      ebEnvironment: selectedBeanstalkEnvironment.value,
      limit: beanstalkLimit.value,
      // Convert form values to ISO format
      startTime: new Date(beanstalkStartTime.value).toISOString(),
      endTime: new Date(beanstalkEndTime.value).toISOString(),
    };

    if (beanstalkLogLevel.value) {
      params.logLevel = beanstalkLogLevel.value as 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
    }
    if (beanstalkCorrelationId.value) {
      params.correlationId = beanstalkCorrelationId.value;
    }
    if (beanstalkUserId.value) {
      params.userId = parseInt(beanstalkUserId.value, 10);
    }

    const response = await adminService.getBeanstalkLogs(params);
    beanstalkLogs.value = response.logs;
    beanstalkLogsCount.value = response.count;
    loadedBeanstalkEnvironment.value = selectedBeanstalkEnvironment.value;
  } catch (err: any) {
    console.error('Failed to load Beanstalk logs:', err);
    
    // Handle specific error messages from the backend
    if (err.response?.data?.error) {
      beanstalkLogsError.value = err.response.data.error;
    } else if (err.message && err.message.includes('does not exist')) {
      beanstalkLogsError.value = `Log group for environment "${selectedBeanstalkEnvironment.value}" does not exist. This environment may not have logging configured in CloudWatch.`;
    } else {
      beanstalkLogsError.value = 'Failed to load Beanstalk logs. Please check your permissions and try again.';
    }
  } finally {
    beanstalkLogsLoading.value = false;
  }
};

const clearBeanstalkFilters = () => {
  beanstalkLogLevel.value = '';
  beanstalkCorrelationId.value = '';
  beanstalkUserId.value = '';
  beanstalkLimit.value = 100;
  // Reset to default 24-hour time range instead of clearing
  setBeanstalkDefaultTimeRange();
};

// Format dates for datetime-local input (YYYY-MM-DDTHH:MM)
const formatForDatetimeLocal = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const setLambdaDefaultTimeRange = () => {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
  
  lambdaStartTime.value = formatForDatetimeLocal(twentyFourHoursAgo);
  lambdaEndTime.value = formatForDatetimeLocal(now);
};

const setBeanstalkDefaultTimeRange = () => {
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));

  beanstalkStartTime.value = formatForDatetimeLocal(twentyFourHoursAgo);
  beanstalkEndTime.value = formatForDatetimeLocal(now);
};

const loadLambdaLogs = async () => {
  if (!selectedLambdaFunction.value) {
    lambdaLogsError.value = 'Please select a Lambda function first.';
    return;
  }

  lambdaLogsLoading.value = true;
  lambdaLogsError.value = null;

  try {
    const params: LambdaLogsParams = {
      function: selectedLambdaFunction.value,
      limit: lambdaLimit.value,
      // Convert form values to ISO format
      startTime: new Date(lambdaStartTime.value).toISOString(),
      endTime: new Date(lambdaEndTime.value).toISOString(),
    };

    if (lambdaLogLevel.value) {
      params.logLevel = lambdaLogLevel.value as 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
    }

    const response = await adminService.getLambdaLogs(params);
    lambdaLogs.value = response.logs;
    lambdaLogsCount.value = response.count;
    loadedLambdaFunction.value = selectedLambdaFunction.value;
  } catch (err) {
    console.error('Failed to load Lambda logs:', err);
    lambdaLogsError.value = 'Failed to load Lambda logs. Please check your permissions and try again.';
  } finally {
    lambdaLogsLoading.value = false;
  }
};

const clearLambdaFilters = () => {
  lambdaLogLevel.value = '';
  lambdaLimit.value = 100;
  // Reset to default 24-hour time range instead of clearing
  setLambdaDefaultTimeRange();
};

const setDlpDefaultTimeRange = () => {
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

  dlpStartDate.value = formatForDatetimeLocal(thirtyDaysAgo);
  dlpEndDate.value = formatForDatetimeLocal(now);
};

const loadDlpLogs = async () => {
  dlpLogsLoading.value = true;
  dlpLogsError.value = null;

  try {
    const params: DlpAuditParams = {
      startDate: new Date(dlpStartDate.value).toISOString(),
      endDate: new Date(dlpEndDate.value).toISOString(),
      categoryID: 1,
    };

    if (dlpUserId.value) {
      params.userId = parseInt(dlpUserId.value, 10);
    }

    dlpLogs.value = await adminService.getDlpAuditLogs(params);
  } catch (err: any) {
    console.error('Failed to load DLP audit logs:', err);

    if (err.response?.data?.error) {
      dlpLogsError.value = `${err.response.data.error}${err.response.data.details ? ': ' + err.response.data.details : ''}`;
    } else {
      dlpLogsError.value = 'Failed to load DLP audit logs. Please check your permissions and try again.';
    }
  } finally {
    dlpLogsLoading.value = false;
  }
};

const clearDlpFilters = () => {
  dlpUserId.value = '';
  setDlpDefaultTimeRange();
};

const formatTimestamp = (timestamp: string) => {
  return new Date(timestamp).toLocaleString();
};

const getLogLevelColor = (level: string) => {
  if (!level) {
    return 'text-gray-600 bg-gray-50 border-gray-200';
  }
  
  switch (level.toLowerCase()) {
    case 'error':
      return 'text-red-600 bg-red-50 border-red-200';
    case 'warn':
      return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    case 'info':
      return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'debug':
      return 'text-gray-600 bg-gray-50 border-gray-200';
    default:
      return 'text-gray-600 bg-gray-50 border-gray-200';
  }
};

const getUserDisplayName = (userId: number): string => {
  return userIdToNameMap.value.get(userId) || `User ${userId}`;
};

const queryParamLabels: Record<string, string> = {
  startDate: 'Start Date',
  endDate: 'End Date',
  coverageType: 'Coverage Type',
  topLevelIndustry: 'Top Level Industry',
  industry: 'Industry',
  wholesalerGroup: 'Wholesaler Group',
  carrier: 'Carrier',
  attachmentPointValueMin: 'Attachment Min',
  attachmentPointValueMax: 'Attachment Max',
  retentionMin: 'Retention Min',
  retentionMax: 'Retention Max',
  accountAttributes: 'Account Attributes',
  agency: 'Agency',
  showExternal: 'Show External',
  activeOnly: 'Active Only',
  ownershipType: 'Ownership Type',
  runoffFlag: 'Runoff',
  peFlag: 'Primary/Excess',
  daysUntilExpiration: 'Days Until Expiration',
  accountStates: 'Account States',
  departments: 'Departments',
  searchQuery: 'Search',
  searchField: 'Search Field',
  servicingRolesSearch: 'Servicing Roles Search',
  includeIndustryChildren: 'Include Industry Children',
};

// Params that are not user-facing filters
const ignoredQueryParams = new Set([
  'limit', 'offset', 'parentOnly', 'sortField', 'sortDirection', 'format',
]);

const filterCriteriaLabels: Record<string, string> = {
  dateRange: 'Date Range',
  coverageTypes: 'Coverage Type',
  wholesalerGroups: 'Wholesaler Group',
  carrierGroups: 'Carrier',
  industries: 'Industry',
  topLevelIndustries: 'Top Level Industry',
  sicIndustries: 'SIC Industry',
  accountAttributes: 'Account Attributes',
  agencies: 'Agency',
  departments: 'Departments',
  ownershipTypes: 'Ownership Type',
  accountStates: 'Account States',
  attachmentRange: 'Attachment Range',
  retentionRange: 'Retention Range',
};

const parseDlpFilters = (queryParams: string | null, metadata: string | null): { label: string; value: string }[] => {
  const results: { label: string; value: string }[] = [];

  // Parse filterCriteria from metadata
  if (metadata) {
    try {
      const parsed = JSON.parse(metadata);
      const criteria = parsed.filterCriteria;
      if (criteria && typeof criteria === 'object') {
        for (const [key, val] of Object.entries(criteria)) {
          if (val == null) continue;
          const label = filterCriteriaLabels[key] || key;
          const value = Array.isArray(val) ? val.join(', ') : String(val);
          if (value) results.push({ label, value });
        }
      }
    } catch {
      // ignore parse errors
    }
  }

  // Parse query params as fallback/supplement
  if (queryParams) {
    const params = new URLSearchParams(queryParams);
    const grouped = new Map<string, string[]>();
    const hasSearch = params.get('searchQuery');

    for (const [key, value] of params.entries()) {
      if (ignoredQueryParams.has(key) || !value) continue;
      if (key === 'searchField' && !hasSearch) continue;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(value);
    }

    const existingLabels = new Set(results.map(r => r.label));
    for (const [key, values] of grouped.entries()) {
      const label = queryParamLabels[key] || key;
      if (existingLabels.has(label)) continue;
      results.push({ label, value: values.join(', ') });
    }
  }

  return results;
};

const getDlpColumns = (metadata: string | null): string[] => {
  if (!metadata) return [];
  try {
    const parsed = JSON.parse(metadata);
    if (Array.isArray(parsed.columns) && parsed.columns.length > 0) {
      return parsed.columns;
    }
  } catch {
    // ignore parse errors
  }
  return [];
};

</script>

<template>
  <div class="min-h-screen bg-linear-to-br from-slate-50 to-blue-50 p-6">
    <div class="mx-auto max-w-7xl space-y-6">
      <!-- Page Header -->
      <div class="space-y-4 text-center">
        
        <h1 class="bg-clip-text text-3xl font-bold">📊 Reporting</h1>
        <p class="text-md mx-auto max-w-2xl text-slate-600">
          View logs from aws cloudwatch
        </p>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-slate-600">Loading data...</span>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-md p-4">
        <div class="flex">
          <AlertCircleIcon class="h-5 w-5 text-red-400 mr-2" />
          <div class="text-sm text-red-800">
            {{ error }}
          </div>
        </div>
      </div>

      <!-- Tabs Navigation -->
      <TabsRoot v-else v-model="currentTab" class="w-full">
        <TabsList
          class="inline-flex h-10 items-center justify-center rounded-md bg-slate-100 p-1 text-slate-600">
          <TabsTrigger
            value="beanstalk"
            class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            <ServerIcon class="h-4 w-4 mr-2" />
            Beanstalk Logs
          </TabsTrigger>
          <TabsTrigger
            value="lambda"
            class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            <FileTextIcon class="h-4 w-4 mr-2" />
            Lambda Logs
          </TabsTrigger>
          <TabsTrigger
            value="dlp"
            class="inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            <ShieldIcon class="h-4 w-4 mr-2" />
            Policy Explorer Downloads
          </TabsTrigger>
        </TabsList>

        <!-- Beanstalk Tab Content -->
        <TabsContent value="beanstalk" class="mt-4">

        <!-- Beanstalk Logs Section -->
        <div class="bg-white rounded-xl shadow-sm border border-slate-200">
          <div class="px-6 py-4 border-b border-slate-200">
            <div class="flex items-center space-x-3">
              <ServerIcon class="h-5 w-5 text-slate-600" />
              <h3 class="text-lg font-semibold text-slate-900">Beanstalk Environment Logs</h3>
            </div>
          </div>

          <div class="p-6">
            <!-- Environment Selection and Filters -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <!-- Environment Selection -->
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-2">
                  Beanstalk Environment *
                </label>
                <Combobox
                  v-model="selectedBeanstalkEnvironment"
                  :options="beanstalkEnvironmentOptions"
                  placeholder="Select an environment" />
              </div>

              <!-- Log Level Filter -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Log Level
                </label>
                <Combobox
                  v-model="beanstalkLogLevel"
                  :options="logLevelOptions"
                  placeholder="All levels" />
              </div>

              <!-- Limit -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Limit
                </label>
                <input
                  v-model.number="beanstalkLimit"
                  type="number"
                  min="1"
                  max="1000"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="100" />
              </div>

              <!-- Start Time -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  v-model="beanstalkStartTime"
                  type="datetime-local"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <!-- End Time -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  v-model="beanstalkEndTime"
                  type="datetime-local"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <!-- User -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  User
                </label>
                <Combobox
                  v-model="beanstalkUserId"
                  :options="userOptions"
                  placeholder="Select a user" />
              </div>

              <!-- Correlation ID -->
              <div class="lg:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Correlation ID
                </label>
                <input
                  v-model="beanstalkCorrelationId"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter correlation ID" />
              </div>
            </div>


            <!-- Action Buttons -->
            <div class="flex gap-3 mb-6">
              <Button
                @click="loadBeanstalkLogs"
                :disabled="!selectedBeanstalkEnvironment || beanstalkLogsLoading">
                <RefreshCwIcon v-if="beanstalkLogsLoading" class="animate-spin h-4 w-4 mr-2" />
                <FileTextIcon v-else class="h-4 w-4 mr-2" />
                {{ beanstalkLogsLoading ? 'Loading...' : 'Load Logs' }}
              </Button>
              
              <button
                @click="clearBeanstalkFilters"
                class="inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm">
                <FilterIcon class="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>

            <!-- Beanstalk Logs Error -->
            <div v-if="beanstalkLogsError" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <div class="flex">
                <AlertCircleIcon class="h-5 w-5 text-red-400 mr-2" />
                <div class="text-sm text-red-800">
                  {{ beanstalkLogsError }}
                </div>
              </div>
            </div>

            <!-- Beanstalk Logs Results -->
            <div v-if="beanstalkLogs && beanstalkLogs.length > 0">
              <div class="mb-4 text-sm text-gray-600">
                Found {{ beanstalkLogsCount }} log{{ beanstalkLogsCount !== 1 ? 's' : '' }} for {{ loadedBeanstalkEnvironment }}
              </div>
              
              <div class="space-y-3 max-h-96 overflow-y-auto">
                <div
                  v-for="(log, index) in beanstalkLogs"
                  :key="index"
                  class="border rounded-md p-4">
                  <div class="mb-2">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                        :class="getLogLevelColor(log.logLevel)"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border shrink-0">
                        {{ log.logLevel || 'UNKNOWN' }}
                      </span>
                      <span class="text-sm text-gray-500 shrink-0">
                        {{ formatTimestamp(log.timestamp) }}
                      </span>
                      <span v-if="log.userId" class="text-xs text-purple-600 font-mono bg-purple-50 px-2 py-1 rounded shrink-0">
                        {{ getUserDisplayName(log.userId) }}
                      </span>
                      <span v-if="log.correlationId" class="text-xs text-blue-600 font-mono bg-blue-50 px-2 py-1 rounded break-all min-w-0">
                        {{ log.correlationId }}
                      </span>
                    </div>
                  </div>
                  <div class="text-sm text-gray-900 font-mono whitespace-pre-wrap bg-gray-50 p-3 rounded border break-words overflow-hidden">
                    {{ log.message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- No Beanstalk Logs State -->
            <div v-else-if="selectedBeanstalkEnvironment && !beanstalkLogsLoading && !beanstalkLogsError" class="text-center py-8">
              <FileTextIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 class="text-lg font-medium text-gray-900 mb-2">No Logs Found</h3>
              <p class="text-gray-500">No logs found for the selected environment and filters.</p>
            </div>

            <!-- Select Environment State -->
            <div v-else-if="!selectedBeanstalkEnvironment && !beanstalkLogsLoading" class="text-center py-8">
              <ServerIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 class="text-lg font-medium text-gray-900 mb-2">Select a Beanstalk Environment</h3>
              <p class="text-gray-500">Choose a Beanstalk environment from the dropdown above to view its logs.</p>
            </div>
          </div>
        </div>
        </TabsContent>

        <!-- Lambda Tab Content -->
        <TabsContent value="lambda" class="mt-4">
        <!-- Lambda Logs Section -->
        <div class="bg-white rounded-xl shadow-sm border border-slate-200">
          <div class="px-6 py-4 border-b border-slate-200">
            <div class="flex items-center space-x-3">
              <FileTextIcon class="h-5 w-5 text-slate-600" />
              <h3 class="text-lg font-semibold text-slate-900">Lambda Function Logs</h3>
            </div>
          </div>

          <div class="p-6">
            <!-- Function Selection and Filters -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <!-- Function Selection -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Lambda Function *
                </label>
                <Combobox
                  v-model="selectedLambdaFunction"
                  :options="lambdaFunctionOptions"
                  placeholder="Select a function" />
              </div>

              <!-- Log Level Filter -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Log Level
                </label>
                <Combobox
                  v-model="lambdaLogLevel"
                  :options="logLevelOptions"
                  placeholder="All levels" />
              </div>

              <!-- Limit -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Limit
                </label>
                <input
                  v-model.number="lambdaLimit"
                  type="number"
                  min="1"
                  max="1000"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="100" />
              </div>

              <!-- Start Time -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  v-model="lambdaStartTime"
                  type="datetime-local"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
              </div>

              <!-- End Time -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  v-model="lambdaEndTime"
                  type="datetime-local"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="flex gap-3 mb-6">
              <Button
                @click="loadLambdaLogs"
                :disabled="!selectedLambdaFunction || lambdaLogsLoading">
                <RefreshCwIcon v-if="lambdaLogsLoading" class="animate-spin h-4 w-4 mr-2" />
                <FileTextIcon v-else class="h-4 w-4 mr-2" />
                {{ lambdaLogsLoading ? 'Loading...' : 'Load Logs' }}
              </Button>
              
              <button
                @click="clearLambdaFilters"
                class="inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm">
                <FilterIcon class="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>

            <!-- Lambda Logs Error -->
            <div v-if="lambdaLogsError" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <div class="flex">
                <AlertCircleIcon class="h-5 w-5 text-red-400 mr-2" />
                <div class="text-sm text-red-800">
                  {{ lambdaLogsError }}
                </div>
              </div>
            </div>

            <!-- Lambda Logs Results -->
            <div v-if="lambdaLogs && lambdaLogs.length > 0">
              <div class="mb-4 text-sm text-gray-600">
                Found {{ lambdaLogsCount }} log{{ lambdaLogsCount !== 1 ? 's' : '' }} for {{ loadedLambdaFunction }}
              </div>
              
              <div class="space-y-3 max-h-96 overflow-y-auto">
                <div
                  v-for="(log, index) in lambdaLogs"
                  :key="index"
                  class="border rounded-md p-4">
                  <div class="mb-2">
                    <div class="flex flex-wrap items-center gap-2">
                      <span
                        :class="getLogLevelColor(log.logLevel)"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border shrink-0">
                        {{ log.logLevel || 'UNKNOWN' }}
                      </span>
                      <span class="text-sm text-gray-500 shrink-0">
                        {{ formatTimestamp(log.timestamp) }}
                      </span>
                    </div>
                  </div>
                  <div class="text-sm text-gray-900 font-mono whitespace-pre-wrap bg-gray-50 p-3 rounded border break-words overflow-hidden">
                    {{ log.message }}
                  </div>
                </div>
              </div>
            </div>

            <!-- No Lambda Logs State -->
            <div v-else-if="selectedLambdaFunction && !lambdaLogsLoading && !lambdaLogsError" class="text-center py-8">
              <FileTextIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 class="text-lg font-medium text-gray-900 mb-2">No Logs Found</h3>
              <p class="text-gray-500">No logs found for the selected function and filters.</p>
            </div>

            <!-- Select Lambda Function State -->
            <div v-else-if="!selectedLambdaFunction && !lambdaLogsLoading" class="text-center py-8">
              <ServerIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 class="text-lg font-medium text-gray-900 mb-2">Select a Lambda Function</h3>
              <p class="text-gray-500">Choose a Lambda function from the dropdown above to view its logs.</p>
            </div>
          </div>
        </div>
        </TabsContent>

        <!-- Data Loss Prevention Tab Content -->
        <TabsContent value="dlp" class="mt-4">
          <div class="bg-white rounded-xl shadow-sm border border-slate-200">
            <div class="px-6 py-4 border-b border-slate-200">
              <div class="flex items-center space-x-3">
                <ShieldIcon class="h-5 w-5 text-slate-600" />
                <h3 class="text-lg font-semibold text-slate-900">Policy Explorer Downloads</h3>
              </div>
            </div>

            <div class="p-6">
              <!-- DLP Filters -->
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <!-- Start Date -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    v-model="dlpStartDate"
                    type="datetime-local"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                </div>

                <!-- End Date -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    v-model="dlpEndDate"
                    type="datetime-local"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" />
                </div>

                <!-- User -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    User
                  </label>
                  <Combobox
                    v-model="dlpUserId"
                    :options="userOptions"
                    placeholder="Select a user" />
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex gap-3 mb-6">
                <Button
                  @click="loadDlpLogs"
                  :disabled="dlpLogsLoading">
                  <RefreshCwIcon v-if="dlpLogsLoading" class="animate-spin h-4 w-4 mr-2" />
                  <ShieldIcon v-else class="h-4 w-4 mr-2" />
                  {{ dlpLogsLoading ? 'Loading...' : 'Load Logs' }}
                </Button>

                <button
                  @click="clearDlpFilters"
                  class="inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors shadow-sm">
                  <FilterIcon class="h-4 w-4 mr-2" />
                  Clear Filters
                </button>
              </div>

              <!-- DLP Error -->
              <div v-if="dlpLogsError" class="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <div class="flex">
                  <AlertCircleIcon class="h-5 w-5 text-red-400 mr-2" />
                  <div class="text-sm text-red-800">
                    {{ dlpLogsError }}
                  </div>
                </div>
              </div>

              <!-- DLP Results Table -->
              <div v-if="dlpLogs.length > 0">
                <div class="mb-4 text-sm text-gray-600">
                  Found {{ dlpLogs.length }} record{{ dlpLogs.length !== 1 ? 's' : '' }}
                </div>

                <div class="overflow-x-auto max-h-[32rem] overflow-y-auto border rounded-lg">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50 sticky top-0">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request URL</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filters</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Columns</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="(log, index) in dlpLogs" :key="index" class="hover:bg-gray-50">
                        <td class="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                          {{ formatTimestamp(log.createdAt) }}
                        </td>
                        <td class="px-4 py-3 text-sm whitespace-nowrap">
                          <span v-if="log.userId" class="text-xs text-purple-600 font-mono bg-purple-50 px-2 py-1 rounded">
                            {{ getUserDisplayName(log.userId) }}
                          </span>
                          <span v-else class="text-gray-400">—</span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-900 font-mono break-all max-w-xs">
                          {{ log.requestUrl }}
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-600 max-w-sm">
                          <template v-if="parseDlpFilters(log.queryParams, log.metadata).length > 0">
                            <div v-for="(filter, i) in parseDlpFilters(log.queryParams, log.metadata)" :key="i" class="mb-1 last:mb-0">
                              <span class="font-medium text-gray-700">{{ filter.label }}:</span>
                              <span class="ml-1">{{ filter.value }}</span>
                            </div>
                          </template>
                          <span v-else class="text-gray-400">—</span>
                        </td>
                        <td class="px-4 py-3 text-sm text-gray-600 max-w-xs">
                          <template v-if="getDlpColumns(log.metadata).length > 0">
                            <span v-for="(col, i) in getDlpColumns(log.metadata)" :key="i"
                              class="inline-block bg-gray-100 rounded px-1.5 py-0.5 mr-1 mb-1 text-xs font-mono">
                              {{ col }}
                            </span>
                          </template>
                          <span v-else class="text-gray-500">all</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- No Results State -->
              <div v-else-if="!dlpLogsLoading && !dlpLogsError" class="text-center py-8">
                <ShieldIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 class="text-lg font-medium text-gray-900 mb-2">No Audit Logs</h3>
                <p class="text-gray-500">Click "Load Logs" to search for DLP audit records.</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </TabsRoot>
    </div>
  </div>
</template>
